import React, { FC } from 'react';

import StateTag from 'crehana-ui/Tag/StateTag';
import TextLink from 'crehana-ui/Buttons/TextLink';
import { useTranslation } from 'Shared/Hooks/useTranslation';
import Avatar from 'crehana-ui/Avatar';
import Star from 'crehana-icons/Star';
import User from 'crehana-icons/User';
import Heart3 from 'crehana-icons/Heart3';
import Gift2 from 'crehana-icons/Gift2';
import Share from 'crehana-icons/Share';
import ArrowLeft2 from 'crehana-icons/ArrowLeft2';

import { AddToPack } from 'Jsx/Shared/CourseCardV3/components/AddToPack';
import {
  secondaryMain,
  yellowMain,
  baseLighter,
} from 'crehana-ui/styles/colors';
import { Container, Row, Col } from 'crehana-ui/GridV2';
import MembershipMode from 'Jsx/CourseDetail/components/MembershipMode';
import { useCoursesInBundleState } from 'Jsx/ContextApis/CoursesInBundle';

import useCanEnrollWithFirstEnroll from 'Jsx/CourseDetail/hooks/useCanEnrollWithFirstEnroll';
import useIsClient from 'Jsx/Shared/Hooks/useIsClient';
import matchMediaQuery from 'Jsx/Utils/matchMediaQuery';
import { unescapeChars } from 'Jsx/Shared/StringParser';

import CourseDetailPrice from 'Jsx/CourseDetail/components/CourseDetailPrice';

import useCoursePromoCountdown from '../../hooks/useCoursePromoCountdown';

import { Course } from '../../types';
import locales from '../../i18n/localeEs';

const translations = locales.es;

type Type = 'BEST_SELLER' | 'NEW';

const renderTag = (type: Type, t) => {
  if (type === 'BEST_SELLER') {
    return (
      <StateTag
        className="mr-24"
        darkMode={undefined}
        label={t('BEST_SELLER', {
          defaultValue: translations.BEST_SELLER,
        })}
        type="yellow"
      />
    );
  }
  if (type === 'NEW') {
    return (
      <StateTag
        className="mr-24"
        darkMode={undefined}
        label={t('NEW', {
          defaultValue: translations.NEW,
        })}
        type="green"
      />
    );
  }

  return null;
};

const formatDate = date => {
  const d = new Date(date);
  const monthIndex =
    d.getMonth() + 1 < 10 ? `0${d.getMonth() + 1}` : d.getMonth() + 1;
  const year = d.getFullYear();

  return `${monthIndex}/${year}`;
};

interface MainSectionProps {
  course: Course;
  isInWishlist: boolean;
  toggleAddToWishlist(): void;
  showShareDialog(): void;
  showGiftCardDialog(): void;
  BuyButtons: any;
  isBuyable: boolean;
  WishlistButton: React.ReactElement;
  GiftButton: React.ReactElement;
  CartButton: React.ReactElement;
  canShowMembershipFeature: boolean;
  isAppLoading: boolean;
  canShowAddToPackUI: boolean;
  canRenderCountdown?: boolean;
}

const MainSection: FC<MainSectionProps> = ({
  course,
  showShareDialog,
  showGiftCardDialog,
  toggleAddToWishlist,
  isInWishlist,
  isBuyable,
  CartButton,
  GiftButton,
  WishlistButton,
  canShowMembershipFeature,
  isAppLoading,
  BuyButtons,
  canShowAddToPackUI,
  canRenderCountdown,
}) => {
  const { t } = useTranslation();
  const { canEnrollToCourseFreemium } = useCanEnrollWithFirstEnroll();
  const {
    toggleCourseInBundle,
    coursesInBundle,
    isCoursesBundleComplete,
  } = useCoursesInBundleState();

  const { promoRemainingTime } = useCoursePromoCountdown(course.dueDate);
  const enrollmentsVisibility = course.enrollments > 10;

  const isClient = useIsClient();
  const [isSmall, setIsSmall] = React.useState(false);

  React.useEffect(() => {
    if (isClient) {
      const { matches } = matchMediaQuery('sm');

      setIsSmall(matches);
    }
  }, []);

  const Reviews = (
    <p className="flex items-center">
      <Star color={yellowMain} size={20} />
      <span className="font-caption ml-4">{course.score.average}</span>
      {course.score.total > 0 && (
        <span className="ml-4 font-legals text-gray-dark">
          ({course.score.total})
        </span>
      )}
    </p>
  );

  const Enrollments = (
    <p className="flex ml-16 items-center">
      <User size={20} color={secondaryMain} />
      <span className="ml-4 text-12 font-nun font-bold">
        {course.enrollments.toLocaleString()}{' '}
        {t('STUDENTS', {
          defaultValue: translations.STUDENTS,
        })}
      </span>
    </p>
  );

  const SecondaryActionButtons = (
    <div className="flex justify-center md:justify-start">
      {isBuyable && (
        <button
          type="button"
          onClick={toggleAddToWishlist}
          className="mx-12 flex flex-col items-center sm:mx-24"
        >
          <Heart3 color={baseLighter} size={16} />
          <p className="font-subtitle3 mt-4 text-base-lighter">
            {isInWishlist
              ? t('REMOVE', {
                  defaultValue: translations.REMOVE,
                })
              : t('I_WANT_IT', {
                  defaultValue: translations.I_WANT_IT,
                })}
          </p>
        </button>
      )}
      {course.courseType === 'free' ? (
        <button
          type="button"
          onClick={showShareDialog}
          className="mx-12 flex items-center sm:mx-24"
        >
          <Share color={baseLighter} size={16} className="mr-16" />
          <p className="font-subtitle3 mt-4 text-base-lighter">
            {t('SHARE_FREE_COURSE', {
              defaultValue: translations.SHARE_FREE_COURSE,
            })}
          </p>
        </button>
      ) : (
        <button
          type="button"
          onClick={showShareDialog}
          className="mx-12 flex flex-col items-center sm:mx-24"
        >
          <Share color={baseLighter} size={16} />
          <p className="font-subtitle3 mt-4 text-base-lighter">
            {t('SHARE', {
              defaultValue: translations.SHARE,
            })}
          </p>
        </button>
      )}
      {isBuyable && (
        <button
          type="button"
          onClick={showGiftCardDialog}
          className="mx-12 flex flex-col items-center sm:mx-24"
        >
          <Gift2 color={baseLighter} size={16} />
          <p className="mt-4 font-subtitle3 text-base-lighter">
            {t('GIFT', {
              defaultValue: translations.GIFT,
            })}
          </p>
        </button>
      )}
    </div>
  );

  const TransactionInfo = (
    <React.Fragment>
      {canShowMembershipFeature && isBuyable && !isAppLoading ? (
        <div className="relative w-full mt-32">
          <MembershipMode
            course={course}
            GiftButton={GiftButton}
            CartButton={CartButton}
            WishlistButton={WishlistButton}
            mixpanelPage="Course detail v3"
          />
        </div>
      ) : (
        <div id="course-detail-transaction-section">
          {isBuyable && !canEnrollToCourseFreemium && (
            <div className="sm:flex sm:items-baseline md:hidden sm:flex-wrap mb-24 md:mb-16">
              {!promoRemainingTime && canRenderCountdown ? (
                <p className="font-link mb-4 text-red-main w-full">
                  {t('OFFER_END_IN', {
                    defaultValue: translations.OFFER_END_IN,
                  })}{' '}
                  {promoRemainingTime}!
                </p>
              ) : null}
              <div className="flex flex-wrap items-center">
                <CourseDetailPrice />
              </div>
            </div>
          )}
          <div
            className={course.courseType === 'free' ? 'sm:flex md:block' : ''}
          >
            <div className="mt-12 mb-16 md:hidden lg:hidden">
              {isSmall ? (
                <div className="mx-auto" style={{ maxWidth: 600 }}>
                  {BuyButtons}
                </div>
              ) : (
                <div>{BuyButtons}</div>
              )}
            </div>
            {!canEnrollToCourseFreemium && SecondaryActionButtons}
          </div>
        </div>
      )}
    </React.Fragment>
  );

  return (
    <header className="md:bg-gray-lighter md:pt-40">
      <Container>
        <Row>
          <Col
            xs={12}
            sm={10}
            md={6}
            lg={8}
            offset={{ xs: 0, sm: 1, md: 0, lg: 0 }}
          >
            <section id="course-detail-main-section" className="mb-32">
              <div className="mb-24 sm:mb-32" id="course-detail-title-section">
                <h1 className="font-h2">
                  <span className="mr-4">
                    {' '}
                    {t('SEO_TITLE', {
                      defaultValue: translations.SEO_TITLE,
                    })}
                  </span>
                  <span> {course.title}</span>
                </h1>
                {course.subTitle?.length > 0 && (
                  <p className="mt-16 sm:mt-24 lg:pr-112 font-body2">
                    {course.subTitle}
                  </p>
                )}
                <p className="sm:hidden text-gray-dark font-legals mt-16">
                  {t('UPDATED_AT', {
                    defaultValue: translations.UPDATED_AT,
                  })}{' '}
                  {formatDate(course.modifiedDate)}
                </p>
              </div>
              <div className="mb-24 inline-flex items-center text-primary-darker">
                <a
                  href={course.professor.profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Avatar
                    className="flex-shrink-0"
                    alt="profesor"
                    size={64}
                    src={course.professor.profilePictureUrl}
                  />
                </a>
                <div className="ml-16">
                  <p>
                    <a
                      href={course.professor.profileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-subtitle3 text-base-dark"
                    >
                      {unescapeChars(course.professor.fullName)}
                    </a>
                  </p>
                  <p className="font-body2">{course.professor.occupation}</p>
                </div>
              </div>
              <div className="mb-24 sm:mb-32 sm:flex sm:items-center sm:flex-wrap">
                <div className="sm:flex sm:items-center ">
                  {course.courseTag && renderTag(course.courseTag.name, t)}
                  <div className="flex items-center mt-24 mb-16 sm:mb-0 sm:mt-0">
                    {Reviews}
                    {enrollmentsVisibility && Enrollments}
                  </div>
                </div>
                <p className="hidden sm:inline-flex sm:mt-0 sm:ml-16 font-body4 text-gray-dark">
                  {t('UPDATED_AT', {
                    defaultValue: translations.UPDATED_AT,
                  })}{' '}
                  {formatDate(course.modifiedDate)}
                </p>
              </div>

              <React.Fragment>
                {canShowAddToPackUI ? (
                  <div className="text-center text-white lg:hidden">
                    <AddToPack
                      courseInPack={coursesInBundle
                        .map(el => el.originalId)
                        .includes(course.originalId)}
                      validateAddCustomBundle={() => {
                        toggleCourseInBundle(course);
                      }}
                      fullCustomBundle={isCoursesBundleComplete}
                    />
                    <TextLink
                      className="mt-20"
                      onClick={() => {
                        window.history.back();
                      }}
                      leftIcon={{
                        component: ArrowLeft2,
                        size: 13,
                      }}
                    >
                      Ver más cursos
                    </TextLink>
                  </div>
                ) : (
                  TransactionInfo
                )}
              </React.Fragment>
            </section>
          </Col>
          <Col xs={12} md={6} lg={4} offset={{ xs: 0, sm: 0, lg: 1 }} />
        </Row>
      </Container>
    </header>
  );
};

export default React.memo(MainSection);
